import {
  Box,
  Icon,
  IconButton,
  Checkbox as MuiCheckbox,
  useMediaQuery,
} from '@mui/material'
import { ListingPage } from 'components/ListingPage/ListingPage'
import { Column } from 'components/Table/Table'
import { useOrder, useOrderProducts } from 'hooks/useOrders'
import theme from 'lib/theme'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { PickingStatus, Product, ProductIds } from 'types/model/product'
import { OrderPart } from 'types/api/order'
import { ReactComponent as RefundIcon } from 'assets/images/icon-refund.svg'
import { TooltipText } from 'components/TooltipText/TooltipText'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { useAlert } from 'providers/AlertProvider'
import { contentLanguages } from 'lib/languages'
import i18n from 'lib/i18'
import { OrderProductModal } from './OrderProductModal'
import { Checkbox } from './CheckboxColumn'
import { PriceColumn } from './PriceColumn'
import { ChargedColumn } from './ChargedColumn'
import { QuantityColumn } from './QuantityColumn'
import { RefundsColumn } from './RefundsColumn'
import { PickingStatusColumn } from './PickingStatusColumn'
import { ReplaceColumn } from './ReplaceColumn'
import { ProductColumn } from './ProductColumn'

type Id =
  | 'checkbox'
  | 'gtin'
  | 'product'
  | 'allowReplace'
  | 'pickingStatus'
  | 'quantity'
  | 'rowPrice'
  | 'refunds'
  | 'charged'

export const OrderProduct = () => {
  const { t } = useTranslation()
  const { orderId, partId } = useParams()
  const { error, order } = useOrder(orderId as string)
  const { setAlert } = useAlert()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const partInfo =
    (order &&
      order.parts[0].id &&
      order.parts.filter((part: OrderPart) => part.id === partId)[0]) ||
    null

  const currency = partInfo?.deliveryMethod.service?.price?.currency
  const formattedCurrency = currency ? `(${currency})` : ''
  const products = partInfo
    ? [
        ...partInfo.products.filter((e) => !e.productType),
        ...partInfo.products.filter(
          (e) => e.productType && e.productType.length > 1
        ),
      ]
    : []

  function getProductIds(products: Product[]): ProductIds[] {
    const result: ProductIds[] = []

    products.forEach((product) => {
      result.push({
        gtin: product.gtin,
        sku: product.sku,
      })
      if (
        product.pickingStatus === PickingStatus.SUBSTITUTED &&
        product.collectedProduct
      ) {
        result.push({
          gtin: product.collectedProduct.gtin,
          sku: product.collectedProduct.sku,
        })
      }
    })

    return result
  }

  const { productInfo, isLoading } = useOrderProducts(getProductIds(products))

  const [refundProductState, setRefundProductState] = useState<Product[] | []>(
    []
  )

  const [modal, setModal] = useState({
    right: false,
  })

  const handleOpenModal = () => {
    setModal({ right: true })
  }
  const handleCloseModal = () => {
    setModal({ right: false })
  }

  const columns: Column<Id>[] = [
    { id: 'checkbox', label: '', minWidth: 10 },
    {
      id: 'product',
      label: t('orderProduct.product'),
      minWidth: 180,
    },
    {
      id: t('allowReplace'),
      label: t('orderProduct.replacement'),
      minWidth: 100,
    },
    {
      id: 'pickingStatus',
      label: t('orderProduct.status'),
      minWidth: 60,
    },
    {
      id: 'quantity',
      label: t('orderProduct.qty'),
      minWidth: 80,
    },
    {
      id: 'rowPrice',
      label: t('orderProduct.rowPrice'),
      minWidth: 60,
    },
    {
      id: 'refunds',
      label: t('orderProduct.refunds', {
        currency: formattedCurrency,
      }),
      minWidth: 60,
    },
    {
      id: 'charged',
      label: t('orderProduct.charged'),
      minWidth: 60,
    },
  ]

  const handleClickRefund = () => {
    return refundProductState.filter((product) => {
      if (product.refundable) {
        return (
          product.refundable.refundable &&
          product.collectedProduct &&
          Object.keys(product.collectedProduct).length > 0 &&
          product.collectedProduct.capturedPrice?.priceVat > 0
        )
      }
      return (
        product.collectedProduct &&
        Object.keys(product.collectedProduct).length > 0 &&
        product.collectedProduct.capturedPrice?.priceVat > 0
      )
    }).length > 0
      ? handleOpenModal()
      : setAlert(t('orderProduct.chooseChargedProducts'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
  }

  type OfferGroup = {
    offerLineId: string
    groupId: string
    index: number
    lineIds: number[]
  }

  const offerIdCounts: { [key: string]: number } = {}
  const productOfferGroups: OfferGroup[] = products
    .filter((p) => p.extraProduct === 'DISCOUNT')
    .map((p) => {
      const offer = p.collectedProduct?.offers?.[0] || p.offers?.[0]
      if (offer && p.linkedOfferLineIds) {
        const offerName =
          offer.name[i18n.language] ?? offer.name[contentLanguages[0]] ?? null

        if (!offerIdCounts[offerName]) {
          offerIdCounts[offerName] = 0
        }
        offerIdCounts[offerName] += 1

        return {
          offerLineId: p.id,
          groupId: offerName,
          index: offerIdCounts[offerName],
          lineIds: p.linkedOfferLineIds,
        }
      }
      return null
    })
    .filter((p): p is OfferGroup => p !== null)

  const findOfferGroupName = (product: Product): string => {
    const group =
      productOfferGroups.find((group) =>
        group.lineIds.includes(parseInt(product.id, 10))
      ) || productOfferGroups.find((group) => group.offerLineId === product.id)

    if (group) {
      return `${group.groupId} (${group.index})`
    }

    return ''
  }

  // Sort products by their offer group so that products
  // with the same offer group are grouped together and
  // the discount product is shown at the bottom.
  products.sort((a, b) => {
    const aOfferGroup = findOfferGroupName(a)
    const bOfferGroup = findOfferGroupName(b)
    if (aOfferGroup === bOfferGroup) {
      const isAOfferDiscount = a.extraProduct === 'DISCOUNT'
      const isBOfferDiscount = b.extraProduct === 'DISCOUNT'

      if (isAOfferDiscount && !isBOfferDiscount) {
        return 1
      }
      if (!isAOfferDiscount && isBOfferDiscount) {
        return -1
      }
    }
    return aOfferGroup.localeCompare(bOfferGroup)
  })

  const getValue = (product: Product, id: string) => {
    const data = id.split('.').reduce((p, prop) => {
      return p[prop as keyof typeof product] as unknown as Product
    }, product)
    if (id === 'checkbox') {
      return (
        <Checkbox
          refundProductState={refundProductState}
          setRefundProductState={setRefundProductState}
          product={product}
        />
      )
    }
    if (id === 'rowPrice') {
      return <PriceColumn product={product} />
    }
    if (id === 'charged') {
      return <ChargedColumn product={product} />
    }
    if (id === 'quantity') {
      return <QuantityColumn product={product} />
    }
    if (id === 'refunds') {
      return <RefundsColumn product={product} />
    }
    if (id === 'pickingStatus') {
      return <PickingStatusColumn product={product} />
    }
    if (id === 'allowReplace') {
      return <ReplaceColumn product={product} />
    }
    if (id === 'product') {
      return (
        <ProductColumn
          product={product}
          productInfo={productInfo}
          offerName={findOfferGroupName(product)}
        />
      )
    }
    return data
  }

  if (!order || products.length === 0 || productInfo.length === 0 || isLoading)
    return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Box display='flex' flexDirection='column'>
      <Box display='flex' alignItems='center'>
        <MuiCheckbox
          sx={{
            '&.Mui-checked .MuiSvgIcon-root': {
              fill: theme.palette.primary.main,
            },
            marginLeft: theme.spacing(1),
          }}
          inputProps={{
            'aria-label': t('orderProduct.refundAllCheckbox'),
          }}
          checked={products.length === refundProductState.length}
          onClick={() =>
            products.length === refundProductState.length
              ? setRefundProductState([])
              : setRefundProductState(products)
          }
        />
        {refundProductState.length > 0 && (
          <Box ml={1}>
            <TooltipText text={t('orderProduct.refund')}>
              <IconButton
                onClick={() => handleClickRefund()}
                aria-label={t('orderProduct.openModalForRefund')}
                sx={{
                  width: '20px',
                  height: '20px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                  },
                }}>
                <Icon
                  sx={{
                    width: '20px',
                    height: '20px',
                  }}
                  component={RefundIcon}
                />
              </IconButton>
            </TooltipText>
          </Box>
        )}
      </Box>
      <ListingPage
        tableHeight='calc(100vh - 120px)'
        refundProductState={refundProductState}
        productTable
        columns={columns}
        listItems={products}
        displayValues={getValue}
      />
      <OrderProductModal
        partId={partInfo?.id as string}
        orderId={orderId as string}
        productInfo={productInfo}
        handleCloseModal={handleCloseModal}
        modal={modal}
        products={refundProductState}
        paymentType={order?.payment?.type}
        currency={formattedCurrency}
        paymentStatus={order?.payment?.publicStatus}
      />
    </Box>
  )
}
